// src/contexts/AuthContext.tsx
'use client';

import React, { createContext, useContext, useState, useEffect } from 'react';
import * as authApi from '@/lib/api/auth/authApi';
import { useRouter } from 'next/navigation';
import { useGlobalStore } from '@/store/useGlobalStore';

// ============================================================================
// Types
// ============================================================================

interface User {
  userId: string;
  username: string;
  clientId: string;
  level?: string;
  expiredDate?: string;
  role?: string;
}

interface AuthContextType {
  user: User | null;
  token: string | null;
  login: (userId: string, password: string) => Promise<User>;
  logout: () => void;
  isAuthenticated: boolean;
  isLoading: boolean;
}

// ============================================================================
// Context
// ============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ============================================================================
// Provider
// ============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [token, setToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const router = useRouter();

  // ========================================
  // 초기화: localStorage에서 복원
  // ========================================
  useEffect(() => {
    const initAuth = () => {
      const storedToken = localStorage.getItem('accessToken');
      const storedUser = localStorage.getItem('user');

      console.log('🔄 AuthContext 초기화:', {
        hasToken: !!storedToken,
        hasUser: !!storedUser,
      });

      if (storedToken && storedUser) {
        try {
          const parsedUser: User = JSON.parse(storedUser);
          setToken(storedToken);
          setUser(parsedUser);

          console.log('✅ 사용자 정보 복원:', {
            userId: parsedUser.userId,
            clientId: parsedUser.clientId,
            username: parsedUser.username,
          });

          // Zustand store에도 반영
          useGlobalStore.getState().setUser(parsedUser);

        } catch (error) {
          console.error('❌ 사용자 정보 파싱 실패:', error);
          clearStoredAuth();
        }
      }

      setIsLoading(false);
    };

    initAuth();
  }, []);

  // ========================================
  // 로그인
  // ========================================
  const login = async (userId: string, password: string): Promise<User> => {
    console.log('🔐 로그인 시도:', { userId });
const email = userId;
    try {
      // 1. API 호출
      const data = await authApi.login(email, password);
      console.log('📦 로그인 응답:', data);

      const { accessToken, user: loginUser, refreshToken } = data;

      // 2. User 객체 생성
      const userToSave: User = {
        userId: loginUser.userId,
        username: loginUser.username,
        clientId: loginUser.clientId,
        level: loginUser.level,
        expiredDate: loginUser.expiredDate,
        role: loginUser.role,
      };



      // 3. localStorage 저장
      localStorage.setItem('accessToken', accessToken);
      localStorage.setItem('refreshToken', refreshToken);
      localStorage.setItem('user', JSON.stringify(userToSave));

      // 4. State 업데이트
      setToken(accessToken);
      setUser(userToSave);

      // 5. Zustand Store 업데이트
      useGlobalStore.getState().setUser(userToSave);

      // 6. Client 데이터 로드 (선택사항)
      if (userToSave.clientId) {
        try {
          await useGlobalStore.getState().loadClientData(userToSave.clientId);
          console.log('✅ Client 데이터 로드 완료');
        } catch (error) {
          console.warn('⚠️ Client 데이터 로드 실패 (계속 진행)', error);
        }
      }

      // 7. 저장 확인 로그
      console.log('✅ 로그인 완료:', {
        userId: userToSave.userId,
        clientId: userToSave.clientId,
        hasToken: !!localStorage.getItem('accessToken'),
        hasRefreshToken: !!localStorage.getItem('refreshToken'),
      });

      return userToSave;

    } catch (error: any) {
      const message = error.response?.data?.message || '로그인에 실패했습니다.';
      console.error('❌ 로그인 에러:', message);
      throw new Error(message);
    }
  };

  // ========================================
  // 로그아웃
  // ========================================
  const logout = () => {
    console.log('🚪 로그아웃 시작');

    // 1. localStorage 클리어
    clearStoredAuth();

    // 2. State 초기화
    setToken(null);
    setUser(null);

    // 3. Zustand Store 초기화
    useGlobalStore.getState().clearUser();

    console.log('✅ 로그아웃 완료');

    // 4. 로그인 페이지로 이동
    router.push('/login');
  };

  // ========================================
  // Helper: 저장된 인증 정보 삭제
  // ========================================
  const clearStoredAuth = () => {
    localStorage.removeItem('accessToken');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');
  };

  // ========================================
  // Context Value
  // ========================================
  const value: AuthContextType = {
    user,
    token,
    login,
    logout,
    isAuthenticated: !!user && !!token,
    isLoading,
  };

  // ========================================
  // Loading UI
  // ========================================
  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-blue-500 border-t-transparent"></div>
          <p className="mt-4 text-gray-600">인증 정보 확인 중...</p>
        </div>
      </div>
    );
  }

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

// ============================================================================
// Hook
// ============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
